import { useState } from 'react'
import type { ChannelState } from '../api/types'
import { CallModal } from './CallModal'
import { MailToContactModal } from './MailToContactModal'
import { SendMessageModal } from './SendMessageModal'

/** The four ways to reach somebody, wired once.
 *
 *  The lead page and the contact card draw these differently — labelled tiles in a
 *  rail, icon buttons beside a name — but what each one *does* has to be the same,
 *  or an officer learns one page and is wrong about the other. The chrome stays
 *  per-page; the behaviour, the order, the gating and the dialogs live here.
 *
 *  WhatsApp is deliberately absent: there is no composer behind it, and a button
 *  that silently opens the SMS one instead is worse than no button. */

export type OutreachChannel = 'mail' | 'call' | 'sms' | 'email'

export const OUTREACH: { key: OutreachChannel; icon: string; label: string }[] = [
  { key: 'mail', icon: 'local_post_office', label: 'Direct mail' },
  { key: 'call', icon: 'call', label: 'Phone' },
  { key: 'sms', icon: 'sms', label: 'Text' },
  { key: 'email', icon: 'alternate_email', label: 'Email' },
]

/** Greyed whenever the channel is shut, with the server's own reason on the hover.
 *  One rule, so neither page invents its own. */
export function reachable(state?: ChannelState | null): { ok: boolean; why: string } {
  return { ok: !!state?.allowed, why: state?.reason || 'Not available' }
}

export function useOutreach(contactId: number, name: string, opts: {
  phone?: string | null
  onChanged?: () => void
} = {}) {
  const [open, setOpen] = useState<OutreachChannel | null>(null)
  const close = () => setOpen(null)

  return {
    /** Open the dialog for a channel. The caller decides whether the button is live. */
    start: (channel: OutreachChannel) => setOpen(channel),
    /** Every dialog, rendered once by the page. */
    modals: (
      <>
        {open === 'mail' ? (
          <MailToContactModal contactId={contactId} name={name}
            onClose={close} onQueued={opts.onChanged} />
        ) : null}
        {open === 'call' ? (
          <CallModal contactId={contactId} name={name} allowed phone={opts.phone}
            onClose={close} onLogged={opts.onChanged} />
        ) : null}
        <SendMessageModal open={open === 'sms' || open === 'email'}
          channel={open === 'email' ? 'email' : 'sms'} contactId={contactId}
          onClose={close} onSent={opts.onChanged} />
      </>
    ),
  }
}
